'use client';

import React, { useState, useEffect, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface CartItem {
  id: string;
  name: string;
  qty: string;
  dose: string;
  price: number;
  emoji: string;
  flavor?: string;
}

interface PriceOption {
  qty: string;
  dose: string;
  price: number;
}

interface ProductLevel {
  label: string;
  options: PriceOption[];
  image?: string;
}

interface Product {
  id: string;
  emoji: string;
  label: string;
  title: string;
  subtitle: string;
  desc: string;
  color: 'purple' | 'gold' | 'red';
  levels: ProductLevel[];
  flavors: string[];
}

// ─── Data ────────────────────────────────────────────────────────────────────
const PRODUCTS: Product[] = [
{
  id: 'gummies',
  emoji: '🍬',
  label: 'Premium Gummies',
  title: 'Choose Your Level',
  subtitle: 'GUMMIES',
  desc: '',
  color: 'purple',
  levels: [
  {
    label: 'Strong · 50mg',
    image: '/assets/images/unnamed__1_-1779902551232.jpg',
    options: [
    { qty: '10 Gummies', dose: '50mg each · 500mg total', price: 1300 },
    { qty: '20 Gummies', dose: '50mg each · 1000mg total', price: 2500 }]
  }],

  flavors: ['🍍 Pineapple Paradise', '🍓 Strawberry Sensation']
},
{
  id: 'cookies',
  emoji: '/assets/medicine-bottle-oil.svg',
  label: 'Bangi Oils',
  title: 'Choose Your Bangi Oil',
  subtitle: 'BANGI OILS',
  desc: '',
  color: 'gold',
  levels: [
  {
    label: 'Nutty Bliss',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5158430168.jpg',
    options: [
    { qty: '30ml', dose: '30mg THC per teaspoon · Carrier oil: Macadamia oil', price: 1500 }]

  },
  {
    label: 'RSO · Medium Potency',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5197023430.jpg',
    options: [
    { qty: '10ml', dose: '3mg THC per drop · Carrier oil: MCT oil', price: 1500 }]

  },
  {
    label: 'RSO · Strong Potency',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5197023430.jpg',
    options: [
    { qty: '10ml', dose: '10mg THC per drop · Carrier oil: MCT oil', price: 4500 }]

  }],

  flavors: []
},
{
  id: 'brownies',
  emoji: '/assets/vape-liquid.svg',
  label: 'Infused Vaping Liquid',
  title: 'Choose Your Potency',
  subtitle: 'INFUSED VAPING LIQUID',
  desc: 'Infused Vape Liquid (use only for vaping). Please note this product is very mild. Not recommended for people with high tolerance.\n\nIngredients: VG 20%, PG (infused with cannabis) 80%.\n\nRecommended dosage: start with 20-30 puffs (18-25 watts) if you\'re a beginner. You will have to experiment and discover which dosage works best for you. It all depends on how much experience you have and how high your tolerance is.\n\nPlease note that this liquid is only suitable for vaporizers that can handle a high percentage of PG.\n\nPortability and subtlety are some of the main reasons people vape cannabis. While carrying herbs and wax concentrates around can be a nuisance or a legal issue, using a standard vape pen loaded with infused vape liquid is commonplace and will not draw unwanted attention. Plus, it makes for an easy and enjoyable way to imbibe THC, especially on the go.',
  color: 'red',
  levels: [
  {
    label: 'Medium',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/products/790843547/5367667668.jpg',
    options: [
    { qty: '10ml', dose: 'Medium potency', price: 2000 }]

  }],

  flavors: ['🍓 Strawberry']
}];


const REVIEWS = [
{ img: "/assets/images/unnamed-1779901974480.jpg", alt: 'Real customer review screenshot 1' },
{ img: "/assets/images/23476-1779901974220.jpg", alt: 'Real customer review screenshot 2' },
{ img: "/assets/images/27877-1779901974467.jpg", alt: 'Real customer review screenshot 3' },
];


const NAIROBI_ZONES = [
'CBD', 'Westlands', 'Kilimani', 'Lavington', 'Karen', 'Langata',
'South B', 'South C', 'Eastleigh', 'Kasarani', 'Roysambu', 'Ruaka',
'Kileleshwa', 'Parklands', 'Runda', 'Gigiri', 'Muthaiga', 'Syokimau', 'Rongai', 'Other'];


// ─── Colour helpers ──────────────────────────────────────────────────────────
const colorMap = {
  purple: {
    label: 'lbl-p',
    title: 'c-purple',
    actTab: 'act-p',
    pp: 'pp-p',
    addBtn: 'ab-p',
    fchip: 'fchip-p',
    cardBorder: 'border-[rgba(124,58,237,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(124,58,237,0.15)]'
  },
  gold: {
    label: 'lbl-g',
    title: 'c-gold',
    actTab: 'act-g',
    pp: 'pp-g',
    addBtn: 'ab-g',
    fchip: 'fchip-g',
    cardBorder: 'border-[rgba(212,160,23,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(212,160,23,0.12)]'
  },
  red: {
    label: 'lbl-r',
    title: 'c-red',
    actTab: 'act-r',
    pp: 'pp-r',
    addBtn: 'ab-r',
    fchip: 'fchip-p',
    cardBorder: 'border-[rgba(192,57,43,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(192,57,43,0.12)]'
  }
};

const ppColors: Record<string, string> = {
  'pp-p': 'text-[#9D5CF5]',
  'pp-g': 'text-[#F0C84A]',
  'pp-r': 'text-[#FF7B70]'
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function SectionHeader({ emoji, title, sub, colorClass }: {emoji: string;title: string;sub: string;colorClass: string;}) {
  return (
    <div className="text-center mb-14">
      {emoji.startsWith('/') ? (
        <span className="block mb-2 animate-pulse-icon flex justify-center">
          <img src={emoji} alt={sub} className="w-12 h-12 mx-auto" />
        </span>
      ) : (
        <span className="text-5xl block mb-2 animate-pulse-icon">{emoji}</span>
      )}
      <div className={`font-bebas text-[clamp(36px,8vw,80px)] tracking-[3px] ${colorClass}`}>{title}</div>
      <p className="text-text-muted text-[13px] tracking-[1.5px] mt-1">{sub}</p>
    </div>);

}

function ProductCard({ product, onAddToCart }: {product: Product;onAddToCart: (item: CartItem) => void;}) {
  const [activeLevel, setActiveLevel] = useState(0);
  const [selectedFlavor, setSelectedFlavor] = useState<string | null>(null);
  const [flavorWarn, setFlavorWarn] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const c = colorMap[product.color];

  const handleAdd = (opt: PriceOption) => {
    if (product.flavors.length > 0 && !selectedFlavor) {
      setFlavorWarn(true);
      return;
    }
    setFlavorWarn(false);
    onAddToCart({
      id: `${product.id}-${activeLevel}-${opt.qty}`,
      name: `${product.label} ${product.levels[activeLevel].label}`,
      qty: opt.qty,
      dose: opt.dose,
      price: opt.price,
      emoji: product.emoji,
      flavor: selectedFlavor ?? undefined
    });
  };

  return (
    <div className={`bg-card border ${c.cardBorder} rounded-[20px] overflow-hidden shadow-[0_4px_40px_rgba(0,0,0,0.40)] mb-8 transition-shadow duration-300 ${c.cardHover}`}>
      <div className="card-body p-5 sm:p-8 pb-6 sm:pb-9">
        {/* Label */}
        <div className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 mb-4 text-[10px] tracking-[2.5px] uppercase font-medium ${
        product.color === 'purple' ? 'bg-[rgba(124,58,237,0.15)] border border-[rgba(124,58,237,0.25)] text-[#9D5CF5]' :
        product.color === 'gold' ? 'bg-[rgba(212,160,23,0.12)] border border-[rgba(212,160,23,0.25)] text-[#F0C84A]' :
        'bg-[rgba(192,57,43,0.12)] border border-[rgba(192,57,43,0.25)] text-[#FF7B70]'}`
        }>
          {product.emoji.startsWith('/') ? (
            <>
              <img src={product.emoji} alt={product.label} className="w-4 h-4" />
              {product.label}
            </>
          ) : (
            <>
              {product.emoji} {product.label}
            </>
          )}
        </div>

        {/* Title */}
        <div className={`font-bebas text-[clamp(22px,4vw,32px)] tracking-[2px] mb-1 ${c.title}`}>{product.title}</div>

        {/* Level tabs — shown before description for Infused Vaping Liquid */}
        {product.id === 'brownies' && (
          <div className="flex flex-wrap gap-2 mb-4 mt-2">
            {product.levels.map((lv, i) =>
            <button
              key={i}
              className={`lvl-tab ${activeLevel === i ? c.actTab : ''}`}
              onClick={() => setActiveLevel(i)}>
              
                {lv.label}
              </button>
            )}
          </div>
        )}

        {/* Description */}
        {product.desc && (
          <p className="text-text-muted text-[12px] leading-relaxed tracking-[0.5px] mb-4 whitespace-pre-line">{product.desc}</p>
        )}

        {/* Level tabs — shown after description for all other products */}
        {product.id !== 'brownies' && (
          <div className="flex flex-wrap gap-2 mb-5">
            {product.levels.map((lv, i) =>
            <button
              key={i}
              className={`lvl-tab ${activeLevel === i ? c.actTab : ''}`}
              onClick={() => setActiveLevel(i)}>
              
                {lv.label}
              </button>
            )}
          </div>
        )}

        {/* Level product image */}
        {product.levels[activeLevel].image && (
          <div className="mb-5 flex justify-center">
            <div className="relative overflow-hidden rounded-xl cursor-pointer" onClick={() => setLightboxOpen(true)} title="Click to expand">
              <img
                src={product.levels[activeLevel].image}
                alt={`${product.levels[activeLevel].label} product`}
                className="rounded-xl object-contain w-full max-w-xs h-64 shadow-lg border border-[rgba(212,160,23,0.25)] transition-transform duration-300 ease-in-out hover:scale-110"
              />
              {product.levels[activeLevel].label.toLowerCase().includes('strong potency') && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold tracking-[2px] uppercase whitespace-nowrap pointer-events-none"
                  style={{ background: 'rgba(212,160,23,0.85)', color: '#1a1208', border: '1px solid rgba(255,200,60,0.6)', boxShadow: '0 2px 8px rgba(212,160,23,0.4)' }}>
                  Strong Potency
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && product.levels[activeLevel].image && (
          <div
            className="fixed inset-0 z-[500] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.88)' }}
            onClick={() => setLightboxOpen(false)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setLightboxOpen(false)}
                className="absolute -top-4 -right-4 w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold z-10 transition-colors"
                style={{ background: 'rgba(212,160,23,0.25)', border: '1px solid rgba(212,160,23,0.40)' }}
              >
                ✕
              </button>
              <img
                src={product.levels[activeLevel].image}
                alt={`${product.levels[activeLevel].label} product expanded`}
                className="rounded-2xl object-contain shadow-2xl"
                style={{ maxWidth: '90vw', maxHeight: '85vh' }}
              />
            </div>
          </div>
        )}

        {/* Flavors */}
        {product.flavors.length > 0 &&
        <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.07)]">
            <div className="flex items-center gap-2.5 text-[10px] tracking-[3px] uppercase text-text-muted font-medium mb-3.5">
              Select Your {product.label} Flavour
              <span className="flex-1 h-px bg-[rgba(255,255,255,0.07)]" />
            </div>
            <div className="flex flex-wrap gap-2">
              {product.flavors.map((f) =>
            <button
              key={f}
              className={`fchip ${c.fchip} ${selectedFlavor === f ? 'sel' : ''}`}
              onClick={() => {setSelectedFlavor(f);setFlavorWarn(false);}}>
              
                  {f}
                </button>
            )}
            </div>
            {flavorWarn &&
          <p className="text-[11px] text-[#FF7B70] tracking-[1px] mt-2 font-semibold">
                ⚠️ Please select a flavour before adding to cart
              </p>
          }
          </div>
        }

        {/* Price grid */}
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(152px, 1fr))' }}>
          {product.levels[activeLevel].options.map((opt, i) =>
          <div key={i} className="price-opt">
              <span className="font-semibold text-[15px] text-text block mb-0.5">{opt.qty}</span>
              <span className="text-[10px] text-text-muted tracking-[0.8px] block mb-2.5">{opt.dose}</span>
              <span className={`font-bold text-[18px] block mb-2.5 ${ppColors[c.pp]}`}>KSh {opt.price.toLocaleString()}</span>
              <button className={`add-btn ${c.addBtn}`} onClick={() => handleAdd(opt)}>+ ADD TO CART</button>
            </div>
          )}
        </div>
      </div>
    </div>);

}

function CartDrawer({
  open, items, onClose, onRemove





}: {open: boolean;items: CartItem[];onClose: () => void;onRemove: (id: string) => void;}) {
  const total = items.reduce((s, i) => s + i.price, 0);

  const handleCheckout = () => {
    if (items.length === 0) return;
    const lines = items.map((i) => `${i.name} — ${i.qty}${i.flavor ? ` (${i.flavor})` : ''} — KSh ${i.price.toLocaleString()}`).join('%0A');
    const msg = `Hello! I'd like to order:%0A${lines}%0A%0ATotal: KSh ${total.toLocaleString()}`;
    window.open(`https://wa.me/254785139571?text=${msg}`, '_blank');
  };

  return (
    <>
      <div className={`cart-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`cart-drawer ${open ? 'open' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b-2 border-[#D4A017]" style={{ background: 'linear-gradient(135deg, #3B0E8A, #7C3AED)' }}>
          <span className="font-bebas text-xl tracking-[3px] text-[#F0C84A]">🛒 YOUR ORDER</span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[rgba(255,255,255,0.10)] text-white text-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.25)] transition-colors">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ?
          <div className="text-center py-12 text-text-muted">
              <span className="text-5xl block mb-4 opacity-50">🛒</span>
              <p>Your cart is empty.</p>
              <p>Add items from the menu!</p>
            </div> :

          items.map((item) =>
          <div key={item.id} className="flex items-start gap-3 py-3 border-b border-[rgba(255,255,255,0.06)] animate-slide-in">
                <div className="flex-1">
                  <p className="font-semibold text-[13px] text-text mb-0.5">
                    {item.emoji.startsWith('/') ? <img src={item.emoji} alt="" className="inline w-4 h-4 mr-1" /> : item.emoji} {item.name}
                  </p>
                  <p className="text-[11px] text-text-muted">{item.qty} · {item.dose}</p>
                  {item.flavor && <p className="text-[11px] text-text-muted">{item.flavor}</p>}
                  <p className="font-bold text-[14px] mt-1 text-[#9D5CF5]">KSh {item.price.toLocaleString()}</p>
                </div>
                <button
              onClick={() => onRemove(item.id)}
              className="w-6 h-6 rounded-full bg-[rgba(192,57,43,0.15)] text-[#FF7B70] text-sm flex items-center justify-center hover:bg-[#E74C3C] hover:text-white transition-all mt-0.5 flex-shrink-0">
              ×</button>
              </div>
          )
          }
        </div>

        {/* Footer */}
        <div className="border-t border-[rgba(212,160,23,0.20)] px-6 py-5 bg-[rgba(0,0,0,0.30)]">
          <div className="flex justify-between items-center mb-4">
            <span className="text-[12px] tracking-[2px] uppercase text-text-muted">Total</span>
            <span className="font-bebas text-[28px] tracking-[1px] text-[#F0C84A]">KSh {total.toLocaleString()}</span>
          </div>
          <button
            onClick={handleCheckout}
            className="w-full py-3.5 rounded-xl text-white text-[13px] tracking-[2px] font-semibold transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #1A8A4A, #145C33)', boxShadow: '0 4px 20px rgba(26,138,74,0.35)' }}>
            <span className="flex items-center justify-center gap-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-6 h-6 flex-shrink-0">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.742 5.494 2.043 7.8L.057 31.428a.75.75 0 0 0 .921.921l7.697-1.984A15.937 15.937 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.773-1.853l-.484-.29-5.03 1.297 1.33-4.868-.317-.503A13.27 13.27 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.274-9.878c-.406-.203-2.403-1.186-2.775-1.322-.373-.136-.644-.203-.915.203-.272.406-1.05 1.322-1.286 1.593-.237.271-.474.305-.88.102-.406-.203-1.716-.633-3.268-2.017-1.208-1.078-2.024-2.41-2.261-2.816-.237-.406-.025-.625.178-.828.183-.182.406-.474.61-.711.203-.237.271-.406.406-.677.136-.271.068-.508-.034-.711-.102-.203-.915-2.205-1.253-3.019-.33-.792-.666-.685-.915-.697-.237-.011-.508-.014-.779-.014-.271 0-.711.102-1.083.508-.373.406-1.422 1.389-1.422 3.389s1.456 3.932 1.659 4.203c.203.271 2.866 4.376 6.944 6.136.97.419 1.727.669 2.317.855.973.31 1.859.267 2.559.162.781-.116 2.403-.983 2.743-1.932.339-.949.339-1.762.237-1.932-.102-.169-.373-.271-.779-.474z"/>
              </svg>
              ORDER VIA WHATSAPP
            </span>
          </button>
          <p className="text-[11px] text-[#FF7B70] text-center mt-2.5 tracking-[1.5px] font-semibold uppercase">Full payment required on all orders</p>
        </div>
      </div>
    </>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Home() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fade-up observer
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('vis');}),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-up').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2800);
  };

  const addToCart = (item: CartItem) => {
    setCart((prev) => [...prev, { ...item, id: `${item.id}-${Date.now()}` }]);
    showToast(item.emoji.startsWith('/') ? `🛒 Added to cart!` : `${item.emoji} Added to cart!`);
  };

  const removeFromCart = (id: string) => setCart((prev) => prev.filter((i) => i.id !== id));

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="relative z-[1]">
      {/* ── Header ── */}
      <header className="sticky top-0 z-[200] flex items-center justify-between px-4 sm:px-6 h-[62px]" style={{ background: 'rgba(10,6,18,0.88)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(212,160,23,0.25)' }}>
        <div className="font-bebas text-[clamp(18px,3.5vw,24px)] tracking-[3px] flex items-center gap-2 grad-purple-gold">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="1em" height="1em" style={{display:'inline-block', verticalAlign:'middle'}}>
              <path d="M32 54 C32 54 6 38 6 22 C6 13 13 7 20 7 C25 7 29 10 32 14 C35 10 39 7 44 7 C51 7 58 13 58 22 C58 38 32 54 32 54Z" fill="#e05a8a" stroke="#c0336a" strokeWidth="1.5"/>
              <path d="M20 16 Q26 20 24 28" stroke="#f9a8d4" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.7"/>
              <path d="M26 12 Q30 16 29 22" stroke="#f9a8d4" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5"/>
              <line x1="32" y1="54" x2="38" y2="62" stroke="#d4a017" strokeWidth="3" strokeLinecap="round"/>
              <line x1="38" y1="62" x2="44" y2="58" stroke="#d4a017" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Alchemy candy
          </div>
        <nav className="hidden md:flex gap-5 items-center">
          {['gummies', 'cookies', 'brownies', 'reviews', 'delivery'].map((s) =>
          <button key={s} onClick={() => scrollTo(s)} className="text-[11px] tracking-[2.5px] uppercase text-text-muted hover:text-[#F0C84A] transition-colors font-medium">
              {s === 'cookies' ? 'bangi oils' : s === 'brownies' ? 'vaping liquid' : s}
            </button>
          )}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 rounded-full px-3 sm:px-4 py-2 text-[12px] tracking-[1.5px] font-medium text-[#F0C84A] transition-all hover:bg-[rgba(212,160,23,0.20)]"
            style={{ background: 'rgba(212,160,23,0.10)', border: '1px solid rgba(212,160,23,0.30)' }}>
            🛒 <span className="hidden sm:inline">Cart</span>
            <span className={`bg-[#E74C3C] text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${cart.length > 0 ? 'animate-bump' : ''}`}>
              {cart.length}
            </span>
          </button>
          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-[5px] rounded-lg transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <span className={`block w-5 h-0.5 bg-[#F0C84A] transition-transform duration-200 ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block w-5 h-0.5 bg-[#F0C84A] transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-[#F0C84A] transition-transform duration-200 ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </header>

      {/* ── Mobile Nav Dropdown ── */}
      {mobileMenuOpen && (
        <div className="md:hidden sticky top-[62px] z-[199] flex flex-col py-3 px-4 gap-1" style={{ background: 'rgba(10,6,18,0.97)', borderBottom: '1px solid rgba(212,160,23,0.20)', backdropFilter: 'blur(16px)' }}>
          {['gummies', 'cookies', 'brownies', 'reviews', 'delivery'].map((s) =>
            <button
              key={s}
              onClick={() => scrollTo(s)}
              className="text-left py-3 px-4 rounded-xl text-[12px] tracking-[2px] uppercase text-text-muted hover:text-[#F0C84A] hover:bg-[rgba(212,160,23,0.07)] transition-all font-medium"
            >
              {s === 'cookies' ? '🫙 Bangi Oils' : s === 'brownies' ? '💨 Vaping Liquid' : s === 'gummies' ? '🍬 Gummies' : s === 'reviews' ? '💬 Reviews' : '📍 Delivery'}
            </button>
          )}
        </div>
      )}

      {/* ── Hero ── */}
      <section className="relative z-[1] min-h-[92vh] flex flex-col items-center justify-center text-center px-4 sm:px-6 py-16 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 90% 70% at 50% 40%, rgba(124,58,237,0.12) 0%, transparent 65%), radial-gradient(ellipse 60% 40% at 20% 90%, rgba(212,160,23,0.08) 0%, transparent 60%)' }} />
        {/* Floating ornaments */}
        <span className="absolute text-[clamp(70px,15vw,200px)] opacity-5 top-[6%] left-[2%] animate-float-orn" style={{ animationDelay: '0s' }}>🍬</span>
        <span className="absolute text-[clamp(50px,12vw,160px)] opacity-5 bottom-[8%] right-[3%] animate-float-orn" style={{ animationDelay: '3s' }}>🌿</span>
        <span className="absolute text-[clamp(40px,8vw,100px)] opacity-5 top-[25%] right-[6%] animate-float-orn" style={{ animationDelay: '1.5s' }}>✨</span>
        <span className="absolute text-[clamp(35px,7vw,90px)] opacity-5 bottom-[25%] left-[6%] animate-float-orn" style={{ animationDelay: '2.5s' }}>🍪</span>

        <div className="relative z-[1] w-full max-w-2xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border rounded-full px-4 sm:px-5 py-1.5 text-[10px] tracking-[2.5px] sm:tracking-[3.5px] uppercase font-medium mb-6 text-[#F0C84A]" style={{ borderColor: 'rgba(212,160,23,0.35)', background: 'rgba(212,160,23,0.07)' }}>
✨ Premium Cannabis Edibles ✨
          </div>

          {/* H1 */}
          <h1 className="font-bebas leading-[0.92] tracking-[3px] mb-3" style={{ fontSize: 'clamp(60px,16vw,160px)' }}>
            <span className="block grad-purple-mid">Alchemy</span>
            <span className="block grad-gold" style={{ fontSize: '0.55em', letterSpacing: '8px' }}>Candy</span>
          </h1>

          {/* Tagline */}
          <p className="font-playfair italic text-text-muted mb-10" style={{ fontSize: 'clamp(14px,2.5vw,20px)' }}>
            Candy & Oils infused with love.
          </p>

          {/* Pills */}
          <div className="flex flex-wrap gap-2 sm:gap-2.5 justify-center mb-10">
            {[
            { label: '🍬 Gummies', cls: 'bg-[rgba(124,58,237,0.12)] border-[rgba(124,58,237,0.30)] text-[#9D5CF5]' },
            { label: 'bangi-oils-svg', cls: 'bg-[rgba(212,160,23,0.10)] border-[rgba(212,160,23,0.30)] text-[#F0C84A]' },
            { label: 'vape-svg', cls: 'bg-[rgba(192,57,43,0.10)] border-[rgba(192,57,43,0.30)] text-[#FF7B70]' }].
            map((p) =>
            <span key={p.label} className={`px-4 sm:px-5 py-2 rounded-full text-[11px] sm:text-[12px] tracking-[1.5px] font-medium border flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform cursor-default ${p.cls}`}>
                {p.label === 'bangi-oils-svg' ? (
                  <><img src="/assets/medicine-bottle-oil.svg" alt="Bangi Oils" className="w-4 h-4" /> Bangi Oils</>
                ) : p.label === 'vape-svg' ? (
                  <><img src="/assets/vape-liquid.svg" alt="Infused Vaping Liquid" className="w-4 h-4" /> Infused Vaping Liquid</>
                ) : p.label}
              </span>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={() => scrollTo('gummies')}
            className="inline-flex items-center gap-2.5 text-white px-7 sm:px-8 py-3.5 rounded-full text-[13px] tracking-[2px] font-medium transition-all hover:-translate-y-0.5 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #D4A017, #F5C842)', color: '#1a0a00', boxShadow: '0 6px 30px rgba(212,160,23,0.55)', border: '1px solid rgba(255,255,255,0.30)' }}>
            
            🍬 Explore Our Menu
          </button>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 flex flex-col items-center gap-1.5 text-text-muted text-[10px] tracking-[3px] uppercase z-[1] animate-scroll-bob">
          MENU
          <span className="block w-px h-8" style={{ background: 'linear-gradient(to bottom, #D4A017, transparent)' }} />
        </div>
      </section>

      <div className="divider" />

      {/* ── Products ── */}
      {PRODUCTS.map((product, idx) =>
      <React.Fragment key={product.id}>
          <section id={product.id} className="relative z-[1] max-w-[1180px] mx-auto px-4 sm:px-6 py-12 sm:py-20 fade-up">
            <SectionHeader
            emoji={product.emoji}
            title={product.subtitle}
            sub={`${product.levels.length} ${product.levels.length === 1 ? 'potency level' : 'potency levels'} · ${product.flavors.length > 0 ? `${product.flavors.length} flavours · ` : ''}Handcrafted`}
            colorClass={colorMap[product.color].title} />
          
            <ProductCard product={product} onAddToCart={addToCart} />
          </section>
          {idx < PRODUCTS.length - 1 && <div className="divider" />}
        </React.Fragment>
      )}

      {/* ── Reviews ── */}
      <div id="reviews" className="relative z-[1] py-12 sm:py-20 px-4 sm:px-6 fade-up" style={{ background: 'linear-gradient(160deg, #0E0820 0%, #130A28 100%)', borderTop: '1px solid rgba(212,160,23,0.20)', borderBottom: '1px solid rgba(212,160,23,0.20)' }}>
        <SectionHeader emoji="💬" title="WHAT THEY SAY" sub="Real customers · Real reactions · No cap" colorClass="c-gold" />
        <div className="max-w-[1140px] mx-auto grid gap-4 sm:gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))' }}>
          {REVIEWS.map((r, i) =>
          <div key={i} className="review-card">
              <div className="relative overflow-hidden">
                <img src={r.img} alt={r.alt} className="w-full object-cover object-top" style={{ height: '360px' }} />
              </div>
            </div>
          )}
        </div>
        <p className="text-center mt-8 text-text-muted text-[13px] font-playfair italic tracking-[0.5px]">
          Join hundreds of happy customers — place your order today ✨
        </p>

        {/* Community CTA removed */}
      </div>

      {/* ── Delivery ── */}
      <section id="delivery" className="relative z-[1] max-w-[1180px] mx-auto px-4 sm:px-6 py-12 sm:py-20 fade-up">
        <SectionHeader emoji="📍" title="DELIVERY" sub="Select your area · We deliver across Nairobi" colorClass="c-gold" />
        <div className="max-w-[700px] mx-auto bg-card border border-[rgba(212,160,23,0.20)] rounded-[20px] p-5 sm:p-8 shadow-[0_4px_40px_rgba(0,0,0,0.40)]">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 mb-4 text-[10px] tracking-[2.5px] uppercase font-medium bg-[rgba(212,160,23,0.10)] border border-[rgba(212,160,23,0.25)] text-[#F0C84A]">
            📍 Delivery Zone
          </div>
          <div className="font-bebas text-[clamp(22px,4vw,32px)] tracking-[2px] mb-1 c-gold">Where Are We Delivering?</div>
          <p className="text-text-muted text-[13px] leading-[1.75] mb-6">
            Choose your neighbourhood below. Delivery charges may vary by location and will be confirmed via WhatsApp before dispatch.
          </p>

          <div className="text-[10px] tracking-[3px] uppercase text-text-muted font-medium mb-3.5 flex items-center gap-2.5">
            Select Your Location
            <span className="flex-1 h-px bg-[rgba(255,255,255,0.07)]" />
          </div>

          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
            {NAIROBI_ZONES.map((zone) =>
            <button
              key={zone}
              className={`dloc ${selectedZone === zone ? 'sel-loc' : ''}`}
              onClick={() => setSelectedZone(zone)}>
              
                {zone}
              </button>
            )}
          </div>

          {selectedZone &&
          <div className="mt-6 p-4 rounded-xl bg-[rgba(212,160,23,0.08)] border border-[rgba(212,160,23,0.25)] text-[13px] text-[#F0C84A]">
              📍 Delivering to <strong>{selectedZone}</strong> — Order via WhatsApp to confirm delivery fee.
            </div>
          }

          <button
            onClick={() => {
              const total = cart.reduce((s, i) => s + i.price, 0);
              if (cart.length === 0) {
                const zoneText = selectedZone ? `%0A%0ADelivery Zone: ${selectedZone}` : '';
                window.open(`https://wa.me/254785139571?text=Hello! I'd like to place an order.${zoneText}`, '_blank');
                return;
              }
              const lines = cart.map((i) => `${i.name} — ${i.qty}${i.flavor ? ` (${i.flavor})` : ''} — KSh ${i.price.toLocaleString()}`).join('%0A');
              const zoneText = selectedZone ? `%0A%0ADelivery Zone: ${selectedZone}` : '';
              const msg = `Hello! I'd like to order:%0A${lines}%0A%0ATotal: KSh ${total.toLocaleString()}${zoneText}`;
              window.open(`https://wa.me/254785139571?text=${msg}`, '_blank');
            }}
            className="mt-6 w-full py-3.5 rounded-xl text-white text-[13px] tracking-[2px] font-semibold transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #1A8A4A, #145C33)', boxShadow: '0 4px 20px rgba(26,138,74,0.35)' }}>
            <span className="flex items-center justify-center gap-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-6 h-6 flex-shrink-0">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.742 5.494 2.043 7.8L.057 31.428a.75.75 0 0 0 .921.921l7.697-1.984A15.937 15.937 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.773-1.853l-.484-.29-5.03 1.297 1.33-4.868-.317-.503A13.27 13.27 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.274-9.878c-.406-.203-2.403-1.186-2.775-1.322-.373-.136-.644-.203-.915.203-.272.406-1.05 1.322-1.286 1.593-.237.271-.474.305-.88.102-.406-.203-1.716-.633-3.268-2.017-1.208-1.078-2.024-2.41-2.261-2.816-.237-.406-.025-.625.178-.828.183-.182.406-.474.61-.711.203-.237.271-.406.406-.677.136-.271.068-.508-.034-.711-.102-.203-.915-2.205-1.253-3.019-.33-.792-.666-.685-.915-.697-.237-.011-.508-.014-.779-.014-.271 0-.711.102-1.083.508-.373.406-1.422 1.389-1.422 3.389s1.456 3.932 1.659 4.203c.203.271 2.866 4.376 6.944 6.136.97.419 1.727.669 2.317.855.973.31 1.859.267 2.559.162.781-.116 2.403-.983 2.743-1.932.339-.949.339-1.762.237-1.932-.102-.169-.373-.271-.779-.474z"/>
              </svg>
              ORDER ON WHATSAPP
            </span>
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="relative z-[1] py-10 px-4 sm:px-6 text-center" style={{ background: 'var(--bg2)', borderTop: '1px solid rgba(212,160,23,0.18)' }}>
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="font-bebas text-[clamp(22px,5vw,32px)] tracking-[4px] grad-purple-gold flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="1em" height="1em" style={{display:'inline-block', verticalAlign:'middle'}}>
              <path d="M32 54 C32 54 6 38 6 22 C6 13 13 7 20 7 C25 7 29 10 32 14 C35 10 39 7 44 7 C51 7 58 13 58 22 C58 38 32 54 32 54Z" fill="#e05a8a" stroke="#c0336a" strokeWidth="1.5"/>
              <path d="M20 16 Q26 20 24 28" stroke="#f9a8d4" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.7"/>
              <path d="M26 12 Q30 16 29 22" stroke="#f9a8d4" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5"/>
              <line x1="32" y1="54" x2="38" y2="62" stroke="#d4a017" strokeWidth="3" strokeLinecap="round"/>
              <line x1="38" y1="62" x2="44" y2="58" stroke="#d4a017" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Alchemy Candy
          </span>
        </div>
        <div className="inline-flex items-center gap-1.5 bg-[rgba(192,57,43,0.12)] border border-[rgba(192,57,43,0.25)] text-[#FF7B70] rounded-full px-3.5 py-1 text-[10px] tracking-[2px] mb-4 font-medium">
          🔞 Adults 18+ Only
        </div>
        <div className="flex justify-center mb-4">
          <a
            href="https://www.instagram.com/alchemycandy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-[1px] font-medium transition-all duration-200 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)', color: '#fff' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Follow us on Instagram
          </a>
        </div>
        <p className="text-text-muted text-[12px] leading-[2] tracking-[0.5px]">
          Premium handcrafted cannabis edibles<br />
          Start low · Go slow · Consume responsibly<br />
          <span className="text-[11px]">Full payment required on all orders · Prices in Kenyan Shillings (KSh)</span>
        </p>
      </footer>

      {/* ── Cart Drawer ── */}
      <CartDrawer open={cartOpen} items={cart} onClose={() => setCartOpen(false)} onRemove={removeFromCart} />

      {/* ── Toast ── */}
      <div className={`toast ${toastVisible ? 'show' : ''}`}>{toastMsg}</div>
    </div>);

}